import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import * as z from 'zod';
import { Stack, Button, Box, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { Field, Form } from 'src/components/hook-form';
import { IContactPerson } from './contact-people-selection';

type ContactPersonSchemaType = { name: string; email: string; phoneNumber?: string; };
interface ContactPersonFormProps { initialData?: IContactPerson | null; onSave: (data: IContactPerson) => void; onCancel: () => void; }

export function ContactPersonForm({ initialData, onSave, onCancel }: ContactPersonFormProps) {
  const { t } = useTranslation('site');
  const ContactPersonSchema = z.object({
    name: z.string().min(1, t('form.contactPeople.form.validation.nameRequired')).max(255, t('form.contactPeople.form.validation.nameMaxLength')),
    email: z.string().min(1, t('form.contactPeople.form.validation.emailRequired')).email(t('form.contactPeople.form.validation.emailInvalid')),
    phoneNumber: z.string().max(50, t('form.contactPeople.form.validation.phoneMaxLength')).optional().or(z.literal('')),
  });
  const methods = useForm<ContactPersonSchemaType>({ resolver: zodResolver(ContactPersonSchema), defaultValues: { name: '', email: '', phoneNumber: '' } });
  const { reset, handleSubmit, formState: { isSubmitting, isValid } } = methods;
  useEffect(() => { if (initialData) { reset({ name: initialData.name || '', email: initialData.email || '', phoneNumber: initialData.phoneNumber || '' }); } else { reset({ name: '', email: '', phoneNumber: '' }); } }, [initialData, reset]);
  const onSubmit = handleSubmit(async (data: ContactPersonSchemaType, event) => { 
    event?.preventDefault();
    event?.stopPropagation();
    const contactData: IContactPerson = { ...data, phoneNumber: data.phoneNumber || undefined }; 
    if (initialData?.id) contactData.id = initialData.id; 
    onSave(contactData); 
  });
  return (
    <Form methods={methods} onSubmit={onSubmit}>
  <Stack spacing={2} sx={{ pt: 2, pb: 2 }}>
        <Field.Text 
          name="name" 
          label={t('form.contactPeople.form.fields.name')} 
          placeholder={t('form.contactPeople.form.placeholders.name')} 
          fullWidth 
          required
        />
        <Field.Text 
          name="email" 
          label={t('form.contactPeople.form.fields.email')} 
          placeholder={t('form.contactPeople.form.placeholders.email')} 
          fullWidth 
          type="email" 
          required
        />
        <Field.Text 
          name="phoneNumber" 
          label={t('form.contactPeople.form.fields.phoneNumber')} 
          placeholder={t('form.contactPeople.form.placeholders.phoneNumber')} 
          fullWidth 
        />
        <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 1 }}>
          <Button variant="outlined" onClick={onCancel}>{t('form.contactPeople.form.buttons.cancel')}</Button>
          <LoadingButton 
            type="submit" 
            variant="contained" 
            loading={isSubmitting} 
            disabled={!isValid}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onSubmit(e);
            }}
          >
            {initialData ? t('form.contactPeople.form.buttons.update') : t('form.contactPeople.form.buttons.add')}
          </LoadingButton>
        </Stack>
      </Stack>
    </Form>
  );
}